/**
 * Diamond Search — diamond-search.js
 * Updated to match the forest green modern design.
 */

(function () {
  'use strict';

  const CONTAINER = document.querySelector('.diamond-search');
  const API_URL = CONTAINER?.dataset.api || 'https://diamond-proxy.vercel.app/api/diamond';
  const API_PAGE_SIZE = 500;

  // Price markup (section setting): percentage of the actual diamond price to charge.
  // 100 = actual price (no markup), 300 = 3× actual. Applied to displayed prices AND
  // the price used to create the cart product, so both stay in sync.
  const MARKUP = (parseFloat(CONTAINER?.dataset.markup) || 100) / 100;
  const BASE_PRICE_MAX = 535000;
  const PRICE_MAX = Math.round(BASE_PRICE_MAX * MARKUP);

  // Tax note ("Incl. GST") — mirrors Theme settings → Price tax note.
  const SHOW_TAX_NOTE = CONTAINER?.dataset.showTaxNote === 'true';
  const TAX_NOTE_TEXT = CONTAINER?.dataset.taxNote || '';
  const TAX_NOTE_HTML = (SHOW_TAX_NOTE && TAX_NOTE_TEXT)
    ? ` <span class="price-tax-note">${TAX_NOTE_TEXT}</span>`
    : '';

  /* -------------------------------------------------------
     STATE
  ------------------------------------------------------- */
  const state = {
    page: 1,
    total: 0,
    totalPages: 1,
    loading: false,
    viewMode: 'grid',
    absoluteCaratMin: 0.3,
    absoluteCaratMax: 52,
    absolutePriceMax: PRICE_MAX,
    filters: {
      type: 'lab',
      shapes: [],
      cuts: [],
      colors: [],
      clarities: [],
      labs: [],
      polishes: [],
      symmetries: [],
      caratMin: 0.3,
      caratMax: 52,
      priceMin: 0,
      priceMax: PRICE_MAX,
      sortBy: 'price_asc',
    },
  };

  /* Store all loaded diamonds for view switching */
  let allLoadedDiamonds = [];

  // If filters change while a fetch is in progress, set this flag.
  // The running fetch checks it on completion and re-fetches with the latest filters.
  let _pendingFetch = false;

  /* -------------------------------------------------------
     FETCH DIAMONDS
  ------------------------------------------------------- */
  // The API requires shape, color, clarity, and carat on every request.
  // When no filter is selected, send all possible values so it returns everything.
  const ALL_SHAPES    = 'Round,Oval,Emerald,Cushion,Pear,Radiant,Heart,Princess,Marquise,Asscher';
  const ALL_COLORS    = 'D,E,F,G,H,I,J,K,L,M,N';
  const ALL_CLARITIES = 'FL,IF,VVS1,VVS2,VS1,VS2,SI1,SI2,I1,I2';

  function buildApiParams(pageNum, f) {
    const params = {};
    if (pageNum > 1) params.page = String(pageNum);

    // Shape, color, clarity, carat are required by the API — fall back to "all" when unfiltered
    params.shape   = f.shapes.length > 0
      ? f.shapes.map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(',')
      : ALL_SHAPES;
    params.color   = f.colors.length > 0    ? f.colors.join(',')    : ALL_COLORS;
    params.clarity = f.clarities.length > 0 ? f.clarities.join(',') : ALL_CLARITIES;
    params.carat   = `${f.caratMin}-${f.caratMax}`;

    // Optional discrete filters
    if (f.cuts.length > 0)       params.cut       = f.cuts.join(',');
    if (f.polishes.length > 0)   params.polish    = f.polishes.join(',');
    if (f.symmetries.length > 0) params.symmetry  = f.symmetries.join(',');
    if (f.labs.length > 0)       params.lab       = f.labs.join(',');

    // Price range — slider values are in marked-up terms; the API filters on the
    // actual price, so divide back by MARKUP before querying.
    if (f.priceMin > 0)
      params.totalprice_min = String(Math.floor(f.priceMin / MARKUP));
    if (f.priceMax < state.absolutePriceMax)
      params.totalprice_max = String(Math.ceil(f.priceMax / MARKUP));

    // Sort — API handles server-side
    const sortMap = {
      price_asc:  { field: 'totalprice', order: 'asc' },
      price_desc: { field: 'totalprice', order: 'desc' },
      carat_asc:  { field: 'carat',      order: 'asc' },
      carat_desc: { field: 'carat',      order: 'desc' },
    };
    const sort = sortMap[f.sortBy] || sortMap.price_asc;
    params.sort_field = sort.field;
    params.sort_order = sort.order;

    return params;
  }

  async function fetchPage(pageNum, f) {
    try {
      const params = buildApiParams(pageNum, f);
      const fetchPromise = fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('fetch timeout')), 28000)
      );

      const res = await Promise.race([fetchPromise, timeoutPromise]);
      if (!res.ok) return null;
      const json = await res.json();

      // Normalise response — API may wrap data in different structures
      const raw = Array.isArray(json?.data) ? json.data
                : Array.isArray(json?.diamonds) ? json.diamonds
                : Array.isArray(json) ? json
                : [];
      const total = json?.total ?? json?.count ?? raw.length;

      // Map TDP API field names to our internal diamond schema
      const diamonds = raw.map(d => ({
        diamondId:       String(d.STOCK_ID   || d.stock_id   || d.id          || ''),
        shape:           (d.SHAPE            || d.shape      || '').toLowerCase(),
        carat:           parseFloat(d.CARAT  || d.carat)     || 0,
        price:           (
                           parseFloat(d.TOTAL_PRICE || d.total_price)
                           || (parseFloat(d.PRICE_PER_CTS || d.price_per_cts || 0)
                               * parseFloat(d.CARAT       || d.carat          || 0))
                           || 0
                         ) * MARKUP,
        color:           d.COLOR     || d.color     || '',
        clarity:         d.CLARITY   || d.clarity   || '',
        cut:             d.CUT       || d.cut       || '',
        polish:          d.POLISH    || d.polish    || '',
        symmetry:        d.SYMMETRY  || d.symmetry  || '',
        lab:             d.LAB       || d.lab       || d.CERTIFICATE_LAB || d.certificate_lab || '',
        certificate:     d.CERTIFICATE_NO  || d.certificate_no  || d.REPORT_NO || d.report_no || d.certificate || '',
        certificatelink: d.CERTIFICATE_LINK || d.certificate_link || d.REPORT_LINK || d.report_link || '',
        image:           d.IMAGE_URL || d.image_url || d.IMAGE || d.image || '',
        type:            'lab',
        currencySymbol:  'A$',
      }));

      return { page: pageNum, total, diamonds };
    } catch (e) {
      console.error('[fetchPage]', e);
      return null;
    }
  }

  async function fetchDiamonds(append = false) {
    if (state.loading) { _pendingFetch = true; return; }
    _pendingFetch = false;
    state.loading = true;

    // Snapshot filters at call time — slider input events mutate state.filters
    // mid-fetch; a reference would cause wrong carat/price values after completion.
    const f = {
      ...state.filters,
      shapes:     [...state.filters.shapes],
      colors:     [...state.filters.colors],
      clarities:  [...state.filters.clarities],
      cuts:       [...state.filters.cuts],
      labs:       [...state.filters.labs],
      polishes:   [...state.filters.polishes],
      symmetries: [...state.filters.symmetries],
    };

    if (!append) {
      showSkeletons();
      hideEndOfResults();
      state.page = 1;
    } else {
      showLoadMoreSpinner();
      state.page++;
    }

    try {
      const result = await fetchPage(state.page, f);
      if (!result) throw new Error('No data returned from API');

      state.total = result.total;
      state.totalPages = Math.ceil(result.total / API_PAGE_SIZE) || 1;
      updateTotal(state.total);

      renderDiamonds(result.diamonds, append);

      if (state.page >= state.totalPages) showEndOfResults();

    } catch (err) {
      console.error('[Diamond Search]', err);
      showError();
    } finally {
      state.loading = false;
      hideLoadMoreSpinner();
      // Re-fetch with latest filters if they changed while this fetch was running
      if (_pendingFetch) {
        _pendingFetch = false;
        fetchDiamonds();
      }
    }
  }

  /* -------------------------------------------------------
     RENDER DIAMONDS
  ------------------------------------------------------- */
  function renderDiamonds(diamonds, append = false) {
    const grid = document.getElementById('DiamondGrid');
    const empty = document.getElementById('DiamondEmpty');
    if (!grid) return;

    if (!append) {
      allLoadedDiamonds = diamonds;
    } else {
      allLoadedDiamonds = [...allLoadedDiamonds, ...diamonds];
    }

    if (!append && !diamonds.length) {
      if (grid) grid.innerHTML = '';
      if (empty) empty.removeAttribute('hidden');
      return;
    }

    if (empty) empty.setAttribute('hidden', '');

    if (state.viewMode === 'list') {
      renderListView(allLoadedDiamonds);
    } else {
      if (grid) grid.classList.remove('is-list');
      const html = diamonds.map(d => buildCard(d)).join('');
      if (append) {
        if (grid) grid.insertAdjacentHTML('beforeend', html);
      } else {
        if (grid) grid.innerHTML = html;
      }
      attachCardClicks(grid, allLoadedDiamonds);
    }
  }

  function attachCardClicks(grid, diamonds) {
    if (!grid) return;
    grid.querySelectorAll('.ds-card').forEach(card => {
      if (card.dataset.listener) return;
      card.dataset.listener = 'true';
      card.addEventListener('click', () => {
        const id = card.dataset.diamondId;
        const diamond = diamonds.find(d => d.diamondId === id);
        if (diamond) openModal(diamond);
      });
    });
  }

  function renderListView(diamonds) {
    const grid = document.getElementById('DiamondGrid');
    if (!grid) return;
    grid.classList.add('is-list');
    grid.innerHTML = `
      <div class="ds-list-header">
        <div>Shape</div>
        <div>Carat</div>
        <div>Cut</div>
        <div>Color</div>
        <div>Clarity</div>
        <div>Report</div>
        <div>Price</div>
      </div>
      ${diamonds.map(d => buildListRow(d)).join('')}
    `;
    grid.querySelectorAll('.ds-list-row').forEach(row => {
      row.addEventListener('click', () => {
        const id = row.dataset.diamondId;
        const diamond = diamonds.find(d => d.diamondId === id);
        if (diamond) openModal(diamond);
      });
    });
  }

  /* -------------------------------------------------------
     BUILD CARD HTML (Refined Design)
  ------------------------------------------------------- */
  function buildCard(d) {
    const shape = (d.shape || 'round').toLowerCase();
    const imgSrc = d.image ? escapeHtml(d.image) : getShapeAsset(shape);
    const price = formatPrice(d.price, d.currencySymbol);
    const caratFmt = parseFloat(d.carat).toFixed(2);
    const labName = d.lab || 'IGI';

    return `
      <div class="ds-card" data-diamond-id="${escapeHtml(d.diamondId)}" role="button" tabindex="0">
        <div class="ds-card__img-wrap">
          <img class="ds-card__img" src="${imgSrc}" loading="lazy" onerror="this.src='${getShapeAsset(shape)}'">
          <span class="ds-card__badge-origin">${labName}</span>
        </div>
        <div class="ds-card__body">
          <div class="ds-card__header">
            <h3 class="ds-card__title">${caratFmt} Carat ${capitalize(shape)}</h3>
            <span class="ds-card__price">${price}${TAX_NOTE_HTML}</span>
          </div>
          <div class="ds-card__footer">
            <div class="ds-card__spec">
              <div class="ds-card__spec-label">Lab</div>
              <div class="ds-card__spec-value">${capitalize(d.type || 'Diamond')}</div>
            </div>
            <div class="ds-card__spec">
              <div class="ds-card__spec-label">Color</div>
              <div class="ds-card__spec-value">${escapeHtml(d.color || '–')}</div>
            </div>
            <div class="ds-card__spec">
              <div class="ds-card__spec-label">Clarity</div>
              <div class="ds-card__spec-value">${escapeHtml(d.clarity || '–')}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /* -------------------------------------------------------
     BUILD LIST ROW HTML
  ------------------------------------------------------- */
  function buildListRow(d) {
    const shape = (d.shape || 'round').toLowerCase();
    const imgSrc = d.image ? escapeHtml(d.image) : getShapeAsset(shape);
    const price = formatPrice(d.price, d.currencySymbol);
    const caratFmt = parseFloat(d.carat).toFixed(2);
    const cut = expandGrade(d.cut);

    return `
      <div class="ds-list-row" data-diamond-id="${escapeHtml(d.diamondId)}" role="button" tabindex="0">
        <div class="ds-list-cell ds-list-cell--shape">
          <img class="ds-list-img" src="${imgSrc}" alt="${shape}" loading="lazy" onerror="this.src='${getShapeAsset(shape)}'">
          <span>${capitalize(shape)}</span>
        </div>
        <div class="ds-list-cell">${caratFmt}</div>
        <div class="ds-list-cell">${cut === '–' ? '-' : cut}</div>
        <div class="ds-list-cell">${escapeHtml(d.color || '–')}</div>
        <div class="ds-list-cell">${escapeHtml(d.clarity || '–')}</div>
        <div class="ds-list-cell">${escapeHtml(d.lab || '–')}</div>
        <div class="ds-list-cell ds-list-cell--price">${price}${TAX_NOTE_HTML}</div>
      </div>
    `;
  }

  /* -------------------------------------------------------
     MODAL
  ------------------------------------------------------- */
  function openModal(d) {
    const modal = document.getElementById('DiamondModal');
    const body = document.getElementById('DiamondModalBody');
    if (!modal || !body) return;

    const shape = (d.shape || 'round').toLowerCase();
    const imgSrc = d.image ? escapeHtml(d.image) : getShapeAsset(shape);
    const price = formatPrice(d.price, d.currencySymbol);
    const caratFmt = parseFloat(d.carat).toFixed(2);

    const specs = [
      ['Carat', caratFmt],
      ['Shape', capitalize(d.shape || '–')],
      ['Cut', expandGrade(d.cut)],
      ['Color', d.color || '–'],
      ['Clarity', d.clarity || '–'],
      ['Polish', expandGrade(d.polish)],
      ['Symmetry', expandGrade(d.symmetry)],
      ['Certificate', d.certificate || '–'],
    ];

    const specsHTML = specs.map(([label, val]) => `
      <div class="ds-modal__spec-item">
        <div class="ds-modal__spec-label">${escapeHtml(label)}</div>
        <div class="ds-modal__spec-value">${escapeHtml(String(val))}</div>
      </div>`
    ).join('');

    body.innerHTML = `
      <div class="ds-modal__grid">
        <div class="ds-modal__img-wrap">
          <img class="ds-modal__img" src="${imgSrc}" onerror="this.src='${getShapeAsset(shape)}'">
        </div>
        <div class="ds-modal__info">
          <h2 class="ds-modal__title">${caratFmt}ct ${capitalize(shape)} Diamond</h2>
          <p class="ds-modal__price">${price}${TAX_NOTE_HTML}</p>
          <div class="ds-modal__specs-grid">${specsHTML}</div>
          <div class="ds-modal__actions">
            <button class="ds-modal__add-btn" id="DsModalAddBtn">Add to Cart</button>
            ${d.certificatelink ? `<a class="ds-modal__cert-link" href="${escapeHtml(d.certificatelink)}" target="_blank">Certificate</a>` : ''}
          </div>
        </div>
      </div>
    `;

    document.getElementById('DsModalAddBtn')?.addEventListener('click', () => addToCart(d));
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    const modal = document.getElementById('DiamondModal');
    if (modal) modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
  }

  /* -------------------------------------------------------
     ADD TO CART — creates a Shopify Draft Order via proxy
     so the exact diamond price is used at checkout
  ------------------------------------------------------- */
  async function addToCart(d) {
    const btn = document.getElementById('DsModalAddBtn');
    const overlay = document.getElementById('DsCartOverlay');

    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Adding…';
    }
    overlay?.removeAttribute('hidden');

    try {
      // Step 1: Create a hidden Shopify product with the correct diamond price
      const VARIANT_URL = API_URL.replace('/api/diamond', '/api/create-variant');
      const varRes = await fetch(VARIANT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ diamond: d }),
      });

      if (!varRes.ok) {
        const errData = await varRes.json().catch(() => ({}));
        throw new Error(errData.error || `Server error ${varRes.status}`);
      }

      const { variantId } = await varRes.json();
      if (!variantId) throw new Error('No variant ID returned.');

      // Step 2: Add the variant to the Shopify cart with diamond details as properties
      const properties = {
        'Diamond ID': d.diamondId || '',
        'Shape': (d.shape ? d.shape.charAt(0).toUpperCase() + d.shape.slice(1).toLowerCase() : ''),
        'Carat': parseFloat(d.carat).toFixed(2) + ' ct',
        'Color': d.color || '',
        'Clarity': d.clarity || '',
        'Cut': d.cut || '',
        'Lab': d.lab || '',
        'Certificate': d.certificate || '',
      };

      const cartRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1, properties }),
      });

      if (!cartRes.ok) {
        const errData = await cartRes.json().catch(() => ({}));
        throw new Error(errData.description || errData.message || 'Could not add to cart.');
      }

      closeModal();
      window.location.href = '/cart';

    } catch (err) {
      console.error('[AddToCart]', err);
      alert('Error adding to cart: ' + err.message);
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Add to Cart';
      }
    } finally {
      overlay?.setAttribute('hidden', '');
    }
  }

  /* -------------------------------------------------------
     UI HELPERS
  ------------------------------------------------------- */
  function showSkeletons() {
    const grid = document.getElementById('DiamondGrid');
    if (grid) {
      grid.innerHTML = Array(12).fill(0).map(() => `
        <div class="ds-card ds-card--skeleton">
          <div class="ds-card__img-wrap skeleton-box" style="background:#f5f5f5;aspect-ratio:1;"></div>
          <div class="ds-card__body">
            <div class="skeleton-line" style="width: 70%; height: 10px; background:#eee; margin-bottom:10px;"></div>
            <div class="skeleton-line" style="width: 40%; height: 10px; background:#eee;"></div>
          </div>
        </div>
      `).join('');
    }
  }

  function updateTotal(count) {
    const el = document.getElementById('DiamondTotalCount');
    if (el) el.textContent = count.toLocaleString();
  }

  function showLoadMoreSpinner() {
    document.getElementById('DsLoadMore')?.removeAttribute('hidden');
  }

  function hideLoadMoreSpinner() {
    document.getElementById('DsLoadMore')?.setAttribute('hidden', '');
  }

  function showEndOfResults() {
    document.getElementById('DsEndOfResults')?.removeAttribute('hidden');
  }

  function hideEndOfResults() {
    document.getElementById('DsEndOfResults')?.setAttribute('hidden', '');
  }

  function updateRangeTrack(minId, maxId) {
    const minEl = document.getElementById(minId);
    const maxEl = document.getElementById(maxId);
    if (!minEl || !maxEl) return;
    const min = parseFloat(minEl.min), max = parseFloat(minEl.max);
    const low = parseFloat(minEl.value), high = parseFloat(maxEl.value);
    const pctLow = ((low - min) / (max - min)) * 100;
    const pctHigh = ((high - min) / (max - min)) * 100;
    const track = minEl.closest('.ds-range-wrap')?.querySelector('.ds-range-track__fill');
    if (track) {
      track.style.left = pctLow + '%';
      track.style.width = (pctHigh - pctLow) + '%';
    }
  }

  function resetFilters() {
    // Reset state to defaults
    state.filters.shapes = [];
    state.filters.cuts = [];
    state.filters.colors = [];
    state.filters.clarities = [];
    state.filters.labs = [];
    state.filters.polishes = [];
    state.filters.symmetries = [];
    state.filters.caratMin = state.absoluteCaratMin;
    state.filters.caratMax = state.absoluteCaratMax;
    state.filters.priceMin = 0;
    state.filters.priceMax = state.absolutePriceMax;

    // Reset UI — sliders and number inputs
    const caratMin = document.getElementById('DsCaratMin');
    const caratMax = document.getElementById('DsCaratMax');
    const priceMin = document.getElementById('DsPriceMin');
    const priceMax = document.getElementById('DsPriceMax');
    if (caratMin) { caratMin.value = state.absoluteCaratMin; document.getElementById('DsCaratMinNum').value = state.absoluteCaratMin; }
    if (caratMax) { caratMax.value = state.absoluteCaratMax; document.getElementById('DsCaratMaxNum').value = state.absoluteCaratMax; }
    if (priceMin) { priceMin.value = 0; document.getElementById('DsPriceMinNum').value = 0; }
    if (priceMax) { priceMax.value = state.absolutePriceMax; document.getElementById('DsPriceMaxNum').value = state.absolutePriceMax; }
    updateRangeTrack('DsCaratMin', 'DsCaratMax');
    updateRangeTrack('DsPriceMin', 'DsPriceMax');

    // Reset button active states
    document.querySelectorAll('.ds-grade-btn--active').forEach(b => b.classList.remove('ds-grade-btn--active'));
    document.querySelectorAll('.ds-shape--active').forEach(b => b.classList.remove('ds-shape--active'));
    // Re-activate all "All" buttons
    document.querySelectorAll('.ds-grade-btn[data-value=""]').forEach(b => b.classList.add('ds-grade-btn--active'));

    // Reset empty message back to default
    const msg = document.getElementById('DiamondEmpty')?.querySelector('p');
    if (msg) msg.textContent = 'No diamonds match your filters.';

    state.page = 1;
    fetchDiamonds();
  }

  function showError() {
    const grid = document.getElementById('DiamondGrid');
    if (grid) grid.innerHTML = '';
    const empty = document.getElementById('DiamondEmpty');
    const msg = empty?.querySelector('p');
    if (msg) msg.textContent = 'Something went wrong loading diamonds. Please try again.';
    if (empty) empty.removeAttribute('hidden');
  }

  /* -------------------------------------------------------
     EVENT LISTENERS
  ------------------------------------------------------- */
  function initEvents() {
    /* Grade Buttons — multi-select toggle */
    const FILTER_KEY_MAP = {
      color: 'colors', clarity: 'clarities', cut: 'cuts',
      lab: 'labs', polish: 'polishes', symmetry: 'symmetries',
    };

    document.querySelectorAll('.ds-grade-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const filter = btn.dataset.filter;
        const value  = btn.dataset.value;
        const key    = FILTER_KEY_MAP[filter];
        if (!key) return;

        if (value === '') {
          // "All" button — clear selection for this filter group
          state.filters[key] = [];
          document.querySelectorAll(`.ds-grade-btn[data-filter="${filter}"]`).forEach(b => b.classList.remove('ds-grade-btn--active'));
          btn.classList.add('ds-grade-btn--active');
        } else {
          const arr = state.filters[key];
          const idx = arr.indexOf(value);
          if (idx > -1) {
            arr.splice(idx, 1);
            btn.classList.remove('ds-grade-btn--active');
          } else {
            arr.push(value);
            btn.classList.add('ds-grade-btn--active');
            // Deactivate "All" when a specific value is selected
            document.querySelector(`.ds-grade-btn[data-filter="${filter}"][data-value=""]`)?.classList.remove('ds-grade-btn--active');
          }
          // Re-activate "All" if nothing selected
          if (arr.length === 0) {
            document.querySelector(`.ds-grade-btn[data-filter="${filter}"][data-value=""]`)?.classList.add('ds-grade-btn--active');
          }
        }
        state.page = 1;
        fetchDiamonds();
      });
    });

    /* Shape buttons — multi-select toggle */
    document.querySelectorAll('.ds-shape').forEach(btn => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.value;
        const arr   = state.filters.shapes;
        const idx   = arr.indexOf(value);
        if (idx > -1) {
          arr.splice(idx, 1);
          btn.classList.remove('ds-shape--active');
        } else {
          arr.push(value);
          btn.classList.add('ds-shape--active');
        }
        state.page = 1;
        fetchDiamonds();
      });
    });

    /* Range sliders — sync slider → number input + state */
    ['DsCaratMin', 'DsCaratMax', 'DsPriceMin', 'DsPriceMax'].forEach(id => {
      const slider = document.getElementById(id);
      if (!slider) return;
      slider.addEventListener('input', () => {
        const val = parseFloat(slider.value);
        const numInput = document.getElementById(id + 'Num');
        if (numInput) numInput.value = val;
        if (id.includes('Carat')) {
          if (id.includes('Min')) state.filters.caratMin = val; else state.filters.caratMax = val;
          updateRangeTrack('DsCaratMin', 'DsCaratMax');
        } else {
          if (id.includes('Min')) state.filters.priceMin = val; else state.filters.priceMax = val;
          updateRangeTrack('DsPriceMin', 'DsPriceMax');
        }
      });
      slider.addEventListener('change', () => { state.page = 1; fetchDiamonds(); });
    });

    /* Number inputs — sync number input → slider + state, then fetch */
    ['DsCaratMinNum', 'DsCaratMaxNum', 'DsPriceMinNum', 'DsPriceMaxNum'].forEach(id => {
      const numInput = document.getElementById(id);
      if (!numInput) return;
      const sliderId = id.replace('Num', '');

      numInput.addEventListener('input', () => {
        const val = parseFloat(numInput.value);
        if (isNaN(val)) return;
        const slider = document.getElementById(sliderId);
        if (slider) slider.value = val;
        if (id.includes('Carat')) {
          if (id.includes('Min')) state.filters.caratMin = val; else state.filters.caratMax = val;
          updateRangeTrack('DsCaratMin', 'DsCaratMax');
        } else {
          if (id.includes('Min')) state.filters.priceMin = val; else state.filters.priceMax = val;
          updateRangeTrack('DsPriceMin', 'DsPriceMax');
        }
      });
      numInput.addEventListener('change', () => {
        const val = parseFloat(numInput.value);
        if (isNaN(val)) return;
        state.page = 1;
        fetchDiamonds();
      });
    });

    /* Collapsible headers */
    document.querySelectorAll('.ds-filter-section__hd').forEach(hd => {
      hd.addEventListener('click', () => {
        hd.closest('.ds-filter-section').classList.toggle('ds-filter-section--collapsed');
      });
    });

    /* SortBy — desktop toolbar and mobile drawer stay in sync */
    document.getElementById('DiamondSortBy')?.addEventListener('change', (e) => {
      state.filters.sortBy = e.target.value;
      const drawer = document.getElementById('DiamondSortByDrawer');
      if (drawer) drawer.value = e.target.value;
      state.page = 1;
      fetchDiamonds();
    });

    document.getElementById('DiamondSortByDrawer')?.addEventListener('change', (e) => {
      state.filters.sortBy = e.target.value;
      const desktop = document.getElementById('DiamondSortBy');
      if (desktop) desktop.value = e.target.value;
      state.page = 1;
      fetchDiamonds();
    });

    /* Reset btn */
    document.getElementById('DiamondEmptyReset')?.addEventListener('click', resetFilters);
    document.getElementById('DiamondResetFilters')?.addEventListener('click', resetFilters);

    /* Mobile filter bottom sheet */
    const sidebar   = document.getElementById('DiamondSidebar');
    const backdrop  = document.getElementById('DsSidebarBackdrop');
    const mobileBtn = document.getElementById('DsMobileFilterBtn');
    const closeBtn  = document.getElementById('DiamondSidebarClose');

    function openMobileSidebar() {
      sidebar?.classList.add('is-open');
      backdrop?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }
    function closeMobileSidebar() {
      sidebar?.classList.remove('is-open');
      backdrop?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    mobileBtn?.addEventListener('click', openMobileSidebar);
    backdrop?.addEventListener('click', closeMobileSidebar);
    closeBtn?.addEventListener('click', closeMobileSidebar);

    /* Modal close */
    document.getElementById('DiamondModalClose')?.addEventListener('click', closeModal);
    document.getElementById('DiamondModalBackdrop')?.addEventListener('click', closeModal);

    /* View toggle */
    document.getElementById('DsViewGrid')?.addEventListener('click', () => {
      if (state.viewMode === 'grid') return;
      state.viewMode = 'grid';
      document.getElementById('DsViewGrid').classList.add('ds-view-btn--active');
      document.getElementById('DsViewList').classList.remove('ds-view-btn--active');
      const grid = document.getElementById('DiamondGrid');
      if (grid) {
        grid.classList.remove('is-list');
        grid.innerHTML = allLoadedDiamonds.map(d => buildCard(d)).join('');
        attachCardClicks(grid, allLoadedDiamonds);
      }
    });

    document.getElementById('DsViewList')?.addEventListener('click', () => {
      if (state.viewMode === 'list') return;
      state.viewMode = 'list';
      document.getElementById('DsViewList').classList.add('ds-view-btn--active');
      document.getElementById('DsViewGrid').classList.remove('ds-view-btn--active');
      renderListView(allLoadedDiamonds);
    });
  }

  function initInfiniteScroll() {
    const sentinel = document.getElementById('DsInfiniteSentinel');
    if (!sentinel) return;

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting || state.loading) return;
      if (state.page < state.totalPages) fetchDiamonds(true);
    }, { threshold: 0.1 });

    observer.observe(sentinel);
  }

  function getShapeAsset(shape) {
    const s = (shape || 'round').toLowerCase();
    const key = 'shape' + s.charAt(0).toUpperCase() + s.slice(1);
    return CONTAINER?.dataset[key] || '';
  }

  function formatPrice(price, symbol) {
    return (symbol || '$') + (parseFloat(price) || 0).toLocaleString();
  }

  function expandGrade(code) {
    const map = { EX: 'Excellent', VG: 'Very Good', GD: 'Good', ID: 'Ideal', FR: 'Fair' };
    return map[code] || code || '–';
  }

  function capitalize(str) { return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : ''; }
  function escapeHtml(str) { return str ? String(str).replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m])) : ''; }

  function init() {
    initEvents();
    initInfiniteScroll();
    updateRangeTrack('DsCaratMin', 'DsCaratMax');
    updateRangeTrack('DsPriceMin', 'DsPriceMax');
    fetchDiamonds();
  }

  init();

})();
